import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Paper, Typography, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip,
    FormControl, FormLabel, OutlinedInput, Select, MenuItem, Autocomplete, Switch, FormControlLabel, RadioGroup, Radio,
    CircularProgress, Alert
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import DiagnosisInput from '../../../../components/DiagnosisInput';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import {
    getLabOrderByIdRequest,
    getLabOrderByPatientRequest, resetGetLabOrderByPatient,
    updateLabOrderStatusRequest, resetUpdateLabOrderStatus,
    cancelLabOrderRequest, resetCancelLabOrder,
    addTestsRequest, resetAddTests,
    cancelTestRequest, resetCancelTest,
    editOrderRequest, resetEditOrder,
} from '../../../../redux/modules/laboratory/labOrder/slice';
import {
    getLabResultByPatientRequest, resetGetLabResultByPatient,
} from '../../../../redux/modules/laboratory/labResult/slice';
import {
    getCumulativeReportRequest, resetGetCumulativeReport,
} from '../../../../redux/modules/laboratory/labReport/slice';
import { getLabTestRequest } from '../../../../redux/modules/laboratory/labTest/slice';
import { getLabTestPanelRequest } from '../../../../redux/modules/laboratory/labTestPanel/slice';
import { getInvoiceByIdRequest } from '../../../../redux/modules/invoice/slice';
import {
    addPaymentRequest, resetAddPayment,
    applyVoucherRequest, resetApplyVoucher,
    removeVoucherRequest, resetRemoveVoucher,
} from '../../../../redux/modules/invoice/slice';
import { getVouchersRequest } from '../../../../redux/modules/voucher/slice';
import { fetchAndPrintLabels } from '../../../../utils/printLabels';
import { formatDuration } from '../../../../utils/duration';
import { formatDob } from '../../../../utils/dob';

const statusColors = {
    ordered: 'info', collected: 'warning', received: 'secondary', processing: 'primary',
    resulted: 'success', verified: 'success', released: 'default', cancelled: 'error'
};
const priorityColors = { routine: 'default', urgent: 'warning', stat: 'error' };

// Instants (ordered, verified, paid…) in the clinic's timezone, so every
// screen shows Guyana time whatever the computer's own clock is set to.
const formatDate = (d, tz) => d ? new Date(d).toLocaleString('en-GB', tz ? { timeZone: tz } : undefined) : '-';
const formatDay = (d, tz) => d ? new Date(d).toLocaleDateString('en-GB', tz ? { timeZone: tz } : undefined) : '-';
const formatCurrency = (amount) => `$${Number(amount || 0).toFixed(2)}`;

export default function LabOrderDetail() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.split('/laboratory')[0];
    const { id } = useParams();
    const { getByIdRes, loading, cancelRes, addTestsRes, cancelTestRes, editOrderRes, getByPatientRes, updateStatusRes } = useSelector(state => state.labOrder);
    const { addPaymentRes, applyVoucherRes, removeVoucherRes } = useSelector(state => state.invoice);
    const tz = useSelector(state => state.hospitalConfig?.localization?.timezone);
    // The site's TAT clock, for the Timeline: from collection where it starts
    // there (lisFlow.collectionVerification — the order's tatStartedAt), else
    // from accession; to release under features['lis.tatToRelease'], else to
    // verification.
    const tatFromCollection = useSelector(state => !!state.hospitalConfig?.lisFlow?.collectionVerification);
    const tatToRelease = useSelector(state => !!state.hospitalConfig?.features?.['lis.tatToRelease']);
    const vouchersRes = useSelector(state => state.voucher.getVouchersRes);
    const invoiceData = useSelector(state => state.invoice.getInvoiceByIdRes);
    const allTests = useSelector(state => state.labTest.getRes?.data);
    const allPanels = useSelector(state => state.labTestPanel.getRes?.data);
    const { currentBranch } = useSelector(state => state.branch);
    const patientResults = useSelector(state => state.labResult.getByPatientRes);
    const cumulativeReport = useSelector(state => state.labReport.getCumulativeRes);
    const order = getByIdRes?.data;

    const [tabIndex, setTabIndex] = useState(0);
    const [addTestsOpen, setAddTestsOpen] = useState(false);
    const [selectedNewTests, setSelectedNewTests] = useState([]);
    const [cancelTestOpen, setCancelTestOpen] = useState(false);
    const [cancelTestEntry, setCancelTestEntry] = useState(null);
    const [cancelTestReason, setCancelTestReason] = useState('');
    const [editOpen, setEditOpen] = useState(false);
    const [editFields, setEditFields] = useState({});
    const [cancelOrderOpen, setCancelOrderOpen] = useState(false);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [paymentData, setPaymentData] = useState({ amount: '', method: 'cash', reference: '', notes: '' });
    const [selectedVoucher, setSelectedVoucher] = useState(null);
    const [cumulativeOpen, setCumulativeOpen] = useState(false);
    const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);

    useEffect(() => {
        dispatch(setMainHeading("Lab Order Detail"));
        if (id) {
            dispatch(getLabOrderByIdRequest(id));
            dispatch(getLabTestRequest({ page: 1, limit: 500, searchQuery: '' }));
            dispatch(getLabTestPanelRequest({ page: 1, limit: 100, searchQuery: '' }));
        }
    }, [dispatch, id]);

    // Fetch invoice (and the voucher list to pick from) when order loads
    useEffect(() => {
        if (order?.invoiceId) {
            dispatch(getInvoiceByIdRequest(order.invoiceId));
            dispatch(getVouchersRequest({ page: 1, limit: 500 }));
        }
    }, [dispatch, order?.invoiceId]);

    // Handle action results
    useEffect(() => {
        if (cancelRes) {
            if (cancelRes.success) { toast("Order cancelled"); dispatch(getLabOrderByIdRequest(id)); }
            else toast(cancelRes.message || "Cancel failed");
            dispatch(resetCancelLabOrder());
        }
    }, [cancelRes, dispatch, id]);

    useEffect(() => {
        if (addTestsRes) {
            if (addTestsRes.success) { toast("Tests added"); dispatch(getLabOrderByIdRequest(id)); setAddTestsOpen(false); setSelectedNewTests([]); }
            else toast(addTestsRes.message || "Add failed");
            dispatch(resetAddTests());
        }
    }, [addTestsRes, dispatch, id]);

    useEffect(() => {
        if (cancelTestRes) {
            if (cancelTestRes.success) { toast("Test cancelled"); dispatch(getLabOrderByIdRequest(id)); setCancelTestOpen(false); setCancelTestReason(''); }
            else toast(cancelTestRes.message || "Cancel failed");
            dispatch(resetCancelTest());
        }
    }, [cancelTestRes, dispatch, id]);

    useEffect(() => {
        if (editOrderRes) {
            if (editOrderRes.success) { toast("Order updated"); dispatch(getLabOrderByIdRequest(id)); setEditOpen(false); }
            else toast(editOrderRes.message || "Update failed");
            dispatch(resetEditOrder());
        }
    }, [editOrderRes, dispatch, id]);

    useEffect(() => {
        if (addPaymentRes) {
            if (addPaymentRes.success) {
                toast("Payment recorded");
                setPaymentOpen(false);
                setPaymentData({ amount: '', method: 'cash', reference: '', notes: '' });
                if (order?.invoiceId) dispatch(getInvoiceByIdRequest(order.invoiceId));
            } else toast(addPaymentRes.message || "Payment failed");
            dispatch(resetAddPayment());
        }
    }, [addPaymentRes, dispatch, order?.invoiceId]);

    useEffect(() => {
        if (applyVoucherRes) {
            if (applyVoucherRes.success) {
                toast(applyVoucherRes.message || "Voucher applied");
                setSelectedVoucher(null);
                if (order?.invoiceId) dispatch(getInvoiceByIdRequest(order.invoiceId));
            } else toast(applyVoucherRes.message || "Failed to apply voucher");
            dispatch(resetApplyVoucher());
        }
    }, [applyVoucherRes, dispatch, order?.invoiceId]);

    useEffect(() => {
        if (removeVoucherRes) {
            if (removeVoucherRes.success) {
                toast(removeVoucherRes.message || "Voucher removed");
                if (order?.invoiceId) dispatch(getInvoiceByIdRequest(order.invoiceId));
            } else toast(removeVoucherRes.message || "Failed to remove voucher");
            dispatch(resetRemoveVoucher());
        }
    }, [removeVoucherRes, dispatch, order?.invoiceId]);

    useEffect(() => {
        if (updateStatusRes) {
            if (updateStatusRes.success) { toast("Status updated"); dispatch(getLabOrderByIdRequest(id)); }
            else toast(updateStatusRes.message || "Status update failed");
            dispatch(resetUpdateLabOrderStatus());
        }
    }, [updateStatusRes, dispatch, id]);

    // Fetch patient history when Patient History tab is selected
    useEffect(() => {
        if (tabIndex === 4 && order?.patientId?._id) {
            // Drop the last patient's history first, so it can't show under this one while loading.
            dispatch(resetGetLabOrderByPatient());
            dispatch(resetGetLabResultByPatient());
            dispatch(getLabOrderByPatientRequest(order.patientId._id));
            dispatch(getLabResultByPatientRequest(order.patientId._id));
        }
    }, [tabIndex, order?.patientId?._id, dispatch]);

    if (loading || !order) {
        return <Typography sx={{ mt: 4 }}>Loading...</Typography>;
    }

    const isActive = !['cancelled', 'released'].includes(order.status);
    const invoice = invoiceData?.data;
    // Active, non-expired vouchers for the current branch, to pick from.
    const availableVouchers = (vouchersRes?.data || []).filter(
        v => v.isActive && !v.isDeleted && (!v.expiresAt || new Date(v.expiresAt) >= new Date())
    );

    // Specimen labels go to the lab's label printer. The browser's print dialog
    // still picks the device on every job, so report/invoice printing — which
    // goes to the ordinary office printer — is unaffected.
    const handlePrintLabels = async () => {
        const result = await fetchAndPrintLabels(
            `${process.env.REACT_APP_SERVER_API}api/laboratory/orders/${id}/labels`,
            { authToken: localStorage.getItem("item"), branchId: currentBranch?.id }
        );
        if (!result.ok) toast(result.message);
    };

    const handleCancelOrder = () => setCancelOrderOpen(true);
    const confirmCancelOrder = () => {
        dispatch(cancelLabOrderRequest({ id, reason: 'Cancelled from order detail' }));
        setCancelOrderOpen(false);
    };

    const handleAddTests = () => {
        if (selectedNewTests.length === 0) { toast("Select at least one test or panel"); return; }
        // Panels expand into their component tests server-side; individual tests are
        // added as-is. The backend addTests + saga already accept both.
        const tests = selectedNewTests.filter(o => o.kind === 'test').map(o => o._id);
        const panelIds = selectedNewTests.filter(o => o.kind === 'panel').map(o => o._id);
        dispatch(addTestsRequest({ id, tests, panelIds }));
    };

    const handleCancelTest = () => {
        if (!cancelTestReason.trim()) { toast("Reason is required"); return; }
        dispatch(cancelTestRequest({ orderId: id, testEntryId: cancelTestEntry._id, reason: cancelTestReason }));
    };

    const handleEditOrder = () => {
        dispatch(editOrderRequest({ id, ...editFields }));
    };

    const handlePayment = () => {
        if (!paymentData.amount || Number(paymentData.amount) <= 0) { toast("Enter a valid amount"); return; }
        dispatch(addPaymentRequest({ id: invoice._id, ...paymentData, amount: Number(paymentData.amount) }));
    };

    const handleApplyVoucher = () => {
        if (!selectedVoucher?.code) { toast("Select a voucher"); return; }
        dispatch(applyVoucherRequest({ id: invoice._id, code: selectedVoucher.code }));
    };

    const handleRemoveVoucher = () => {
        dispatch(removeVoucherRequest(invoice._id));
    };

    // Status transition map: current → next allowed statuses
    const statusTransitions = {
        ordered: [{ status: 'collected', label: 'Mark Collected' }],
        collected: [{ status: 'received', label: 'Mark Received' }],
        received: [{ status: 'processing', label: 'Start Processing' }],
        verified: [{ status: 'released', label: 'Release', color: 'success' }],
    };
    const availableTransitions = statusTransitions[order.status] || [];

    const handleStatusTransition = (transition) => {
        setPendingStatus(transition);
        setStatusConfirmOpen(true);
    };
    const confirmStatusTransition = () => {
        dispatch(updateLabOrderStatusRequest({ id, status: pendingStatus.status }));
        setStatusConfirmOpen(false);
        setPendingStatus(null);
    };

    const handleCumulativeReport = () => {
        if (!order?.patientId?._id) return;
        dispatch(getCumulativeReportRequest(order.patientId._id));
        setCumulativeOpen(true);
    };

    // Add-on picker options: orderable panels + orderable individual tests not
    // already on the order. Non-orderable component tests (GTT timepoints, CBC
    // parameters, lipid fractions, …) are excluded — they're only added by
    // ordering their parent panel, which the backend expands. Each option is tagged
    // kind: 'test' | 'panel' so the label/price and submit handle both.
    const existingTestIds = new Set(order.tests?.map(t => (t.testId?._id || t.testId)?.toString()));
    const availableNewTests = [
        ...(allPanels || [])
            .filter(p => p.isOrderable !== false)
            .map(p => ({ kind: 'panel', _id: p._id, code: p.panelCode, name: p.name, price: p.price })),
        ...(allTests || [])
            .filter(t => t.isOrderable !== false && !existingTestIds.has(t._id?.toString()))
            .map(t => ({ kind: 'test', _id: t._id, code: t.testCode, name: t.name, price: t.price })),
    ];

    return (
        <Box sx={{ mt: 2 }}>
            <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => navigate(location.state?.from || `${basePath}/laboratory/orders`)} sx={{ mb: 1 }}>
                Back to Lab Orders
            </Button>
            {/* Order Header */}
            <Paper sx={{ p: 3, mb: 3 }} className="cardStyle">
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <Typography variant="caption" color="text.secondary">Order ID</Typography>
                        <Typography variant="h6" fontWeight={600}>{order.labOrderId}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={1.5}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box><Chip label={order.status} color={statusColors[order.status]} size="small" sx={{ textTransform: 'capitalize' }} /></Box>
                    </Grid>
                    <Grid item xs={6} sm={1.5}>
                        <Typography variant="caption" color="text.secondary">Priority</Typography>
                        <Box><Chip label={order.priority} color={priorityColors[order.priority]} size="small" sx={{ textTransform: 'capitalize' }} /></Box>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {availableTransitions.map(t => (
                            <Button key={t.status} size="small" variant="contained" color={t.color || 'primary'} onClick={() => handleStatusTransition(t)} disabled={loading}>{t.label}</Button>
                        ))}
                        <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintLabels}>Print Labels</Button>
                        {isActive && <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddTestsOpen(true)}>Add Tests</Button>}
                        {isActive && <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => { setEditFields({ priority: order.priority, clinicalNotes: order.clinicalNotes || '', diagnosis: order.diagnosis || '', isFasting: order.isFasting || false, orderingPhysicianName: order.orderingPhysicianName || '', orderingPhysicianPhone: order.orderingPhysicianPhone || '' }); setEditOpen(true); }}>Edit</Button>}
                        {isActive && <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancelOrder}>Cancel Order</Button>}
                    </Grid>
                </Grid>
            </Paper>

            {/* Patient Info */}
            <Paper sx={{ p: 3, mb: 3 }} className="cardStyle">
                <Typography variant="subtitle1" fontWeight={600} mb={1}>Patient</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                        <Typography variant="caption" color="text.secondary">Name</Typography>
                        <Typography>{order.patientId ? `${order.patientId.fName || ''} ${order.patientId.lName || ''}`.trim() : '-'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <Typography variant="caption" color="text.secondary">MRN</Typography>
                        <Typography>{order.patientId?.mrn || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <Typography variant="caption" color="text.secondary">Gender</Typography>
                        <Typography sx={{ textTransform: 'capitalize' }}>{order.patientId?.gender || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <Typography variant="caption" color="text.secondary">DOB</Typography>
                        <Typography>{formatDob(order.patientId?.dob)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Order Type</Typography>
                        <Typography sx={{ textTransform: 'capitalize' }}>{order.orderType} {order.isFasting ? '| Fasting' : ''}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Referring Physician</Typography>
                        <Typography>
                            {order.orderingPhysicianName || '-'}
                            {order.orderingPhysicianPhone ? ` | ${order.orderingPhysicianPhone}` : ''}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }} className="cardStyle">
                <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`Tests (${order.tests?.length || 0})`} />
                    <Tab label={`Specimens (${order.specimens?.length || 0})`} />
                    <Tab label="Billing" />
                    <Tab label="Timeline" />
                    <Tab label="Patient History" />
                </Tabs>
                <Box sx={{ p: 3 }}>
                    {/* Tests Tab */}
                    {tabIndex === 0 && (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Test Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Panel</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order.tests?.map((t, i) => (
                                        <TableRow key={i} sx={t.status === 'cancelled' ? { opacity: 0.5 } : {}}>
                                            <TableCell>{t.testId?.testCode || '-'}</TableCell>
                                            <TableCell>{t.testId?.name || '-'}</TableCell>
                                            <TableCell>{t.panelId?.panelCode || '-'}</TableCell>
                                            <TableCell><Chip label={t.status} size="small" color={statusColors[t.status] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                                            <TableCell align="right">
                                                {t.status !== 'cancelled' && isActive && (
                                                    <Tooltip title="Cancel test">
                                                        <IconButton size="small" color="error" onClick={() => { setCancelTestEntry(t); setCancelTestOpen(true); }}>
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Specimens Tab */}
                    {tabIndex === 1 && (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Accession #</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Collected By</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Received By</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Condition</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Label</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order.specimens?.map((s, i) => (
                                        <TableRow key={i}>
                                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{s.accessionNumber}</TableCell>
                                            <TableCell>
                                                {s.specimenTypeId?.name || '-'}
                                                {s.specimenTypeId?.tubeColor && <Chip label={s.specimenTypeId.tubeColor} size="small" sx={{ ml: 1, textTransform: 'capitalize' }} />}
                                            </TableCell>
                                            <TableCell><Chip label={s.status?.replace(/_/g, ' ')} size="small" sx={{ textTransform: 'capitalize' }} /></TableCell>
                                            <TableCell>{s.collectedBy ? `${s.collectedBy.firstName || ''} ${s.collectedBy.lastName || ''}`.trim() : '-'}</TableCell>
                                            <TableCell>{s.receivedBy ? `${s.receivedBy.firstName || ''} ${s.receivedBy.lastName || ''}`.trim() : '-'}</TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{s.conditionOnReceipt || '-'}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Print label">
                                                    <IconButton size="small" onClick={async () => {
                                                        try {
                                                            const authToken = localStorage.getItem("item");
                                                            const res = await fetch(`${process.env.REACT_APP_SERVER_API}api/laboratory/specimens/${s._id}/label`, {
                                                                headers: { Authorization: `Bearer ${authToken}`, 'X-Current-Branch': currentBranch?.id || '' }
                                                            });
                                                            if (!res.ok) { toast("Failed to load label"); return; }
                                                            const blob = await res.blob();
                                                            window.open(URL.createObjectURL(blob), '_blank');
                                                        } catch { toast("Failed to load label"); }
                                                    }}>
                                                        <LocalPrintshopIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Billing Tab */}
                    {tabIndex === 2 && (
                        <Box>
                            {invoice ? (
                                <>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="caption" color="text.secondary">Invoice #</Typography>
                                            <Typography fontWeight={600}>{invoice.invoiceNumber}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Typography variant="caption" color="text.secondary">Status</Typography>
                                            <Box><Chip label={invoice.status} size="small" sx={{ textTransform: 'capitalize' }} /></Box>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Typography variant="caption" color="text.secondary">Total</Typography>
                                            <Typography fontWeight={600}>{formatCurrency(invoice.totalAmount)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <Typography variant="caption" color="text.secondary">Paid</Typography>
                                            <Typography color="success.main">{formatCurrency(invoice.amountPaid)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Typography variant="caption" color="text.secondary">Balance Due</Typography>
                                            <Typography color={invoice.amountDue > 0 ? 'error.main' : 'success.main'} fontWeight={600}>{formatCurrency(invoice.amountDue)}</Typography>
                                        </Grid>
                                    </Grid>
                                    <TableContainer sx={{ mb: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {invoice.lineItems?.map((li, i) => (
                                                    <TableRow key={i} sx={li.isCancelled ? { opacity: 0.5, textDecoration: 'line-through' } : {}}>
                                                        <TableCell>{li.description}{li.isCancelled ? ' (CANCELLED)' : ''}</TableCell>
                                                        <TableCell align="right">{li.quantity}</TableCell>
                                                        <TableCell align="right">{formatCurrency(li.unitPrice)}</TableCell>
                                                        <TableCell align="right">{formatCurrency(li.lineTotal)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {/* Voucher / Discount code */}
                                    {invoice.status !== 'cancelled' && (
                                        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Voucher / Discount Code</Typography>
                                            {invoice.voucher?.code ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip label={invoice.voucher.code} color="success" variant="outlined" />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {invoice.voucher.discountType === 'percentage'
                                                            ? `${invoice.voucher.value}% off`
                                                            : `${formatCurrency(invoice.voucher.value)} off`}
                                                        {invoice.voucherDiscount > 0 ? ` (−${formatCurrency(invoice.voucherDiscount)})` : ''}
                                                    </Typography>
                                                    <Button size="small" color="error" onClick={handleRemoveVoucher} disabled={loading}>Remove</Button>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <Autocomplete
                                                        size="small"
                                                        sx={{ minWidth: 300 }}
                                                        options={availableVouchers}
                                                        value={selectedVoucher}
                                                        onChange={(e, v) => setSelectedVoucher(v)}
                                                        getOptionLabel={(o) => o?.code || ''}
                                                        isOptionEqualToValue={(o, v) => o._id === v._id}
                                                        noOptionsText="No active vouchers"
                                                        renderInput={(params) => <TextField {...params} label="Select voucher" />}
                                                        renderOption={(props, o) => (
                                                            <li {...props} key={o._id}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                                                                    <Typography variant="body2" fontWeight={600}>{o.code}</Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {o.discountType === 'percentage'
                                                                            ? `${o.value}% off`
                                                                            : `${formatCurrency(o.value)} off`}
                                                                    </Typography>
                                                                </Box>
                                                            </li>
                                                        )}
                                                    />
                                                    <Button size="small" variant="contained" onClick={handleApplyVoucher} disabled={loading || !selectedVoucher}>Apply</Button>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                    {invoice.payments?.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Payments</Typography>
                                            {invoice.payments.map((p, i) => (
                                                <Typography key={i} variant="body2" color="text.secondary">
                                                    {formatDay(p.paidAt, tz)} - {p.method} - {formatCurrency(p.amount)}{p.reference ? ` (Ref: ${p.reference})` : ''}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                    {invoice.amountDue > 0 && invoice.status !== 'cancelled' && (
                                        <Button variant="contained" size="small" onClick={() => { setPaymentData({ ...paymentData, amount: invoice.amountDue }); setPaymentOpen(true); }}>Record Payment</Button>
                                    )}
                                </>
                            ) : (
                                <Typography color="text.secondary">No invoice linked to this order.</Typography>
                            )}
                        </Box>
                    )}

                    {/* Timeline Tab */}
                    {tabIndex === 3 && (
                        <Grid container spacing={1}>
                            {[
                                { label: 'Ordered', date: order.orderedAt },
                                { label: 'Collected', date: order.collectedAt },
                                { label: 'Received', date: order.receivedAt },
                                { label: 'Resulted', date: order.resultedAt },
                                { label: 'Verified', date: order.verifiedAt },
                                { label: 'Released', date: order.releasedAt },
                            ].map((item, i) => (
                                <Grid item xs={6} sm={2} key={i}>
                                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                    <Typography variant="body2">{formatDate(item.date, tz)}</Typography>
                                </Grid>
                            ))}
                            {/* Turnaround on the site's clock (see tatFromCollection / tatToRelease);
                                to verified sites also see accession to released. */}
                            {(() => {
                                const start = tatFromCollection ? (order.tatStartedAt || order.collectedAt) : order.orderedAt;
                                const from = tatFromCollection ? 'collection' : 'accession';
                                return [
                                    { label: `Turnaround — ${from} to ${tatToRelease ? 'released' : 'verified'}`, end: tatToRelease ? order.releasedAt : order.verifiedAt },
                                    ...(tatToRelease ? [] : [{ label: `${from[0].toUpperCase()}${from.slice(1)} to released`, end: order.releasedAt }]),
                                ].filter((t) => t.end && start).map((t) => (
                                    <Grid item xs={12} sm={4} key={t.label} sx={{ mt: 2 }}>
                                        <Typography variant="caption" color="text.secondary">{t.label}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {formatDuration((new Date(t.end) - new Date(start)) / 60000)}
                                        </Typography>
                                    </Grid>
                                ));
                            })()}
                            {order.clinicalNotes && (
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Clinical Notes</Typography>
                                    <Typography variant="body2">{order.clinicalNotes}</Typography>
                                </Grid>
                            )}
                            {order.diagnosis && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Diagnosis</Typography>
                                    <Typography variant="body2">{order.diagnosis}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}

                    {/* Patient History Tab */}
                    {tabIndex === 4 && (
                        <Box>
                            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600}>Past Orders</Typography>
                                <Button size="small" variant="outlined" startIcon={<AssessmentIcon />} onClick={handleCumulativeReport}>
                                    Cumulative Report
                                </Button>
                            </Box>
                            {getByPatientRes?.data?.length > 0 ? (
                                <TableContainer sx={{ mb: 3 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Tests</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {getByPatientRes.data.filter(o => o._id !== order._id).map((o, i) => (
                                                <TableRow key={i} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`${basePath}/laboratory/orders/${o._id}`, { state: { from: location.state?.from } })}>
                                                    <TableCell sx={{ fontFamily: 'monospace' }}>{o.labOrderId}</TableCell>
                                                    <TableCell>{formatDate(o.orderedAt || o.createdAt, tz)}</TableCell>
                                                    <TableCell>
                                                        {o.tests?.slice(0, 3).map((t, j) => (
                                                            <Chip key={j} label={t.testId?.testCode || t.testId?.name || '-'} size="small" sx={{ mr: 0.5 }} />
                                                        ))}
                                                        {o.tests?.length > 3 && <Chip label={`+${o.tests.length - 3}`} size="small" />}
                                                    </TableCell>
                                                    <TableCell><Chip label={o.status} size="small" color={statusColors[o.status] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                                                    <TableCell><Chip label={o.priority} size="small" color={priorityColors[o.priority] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography color="text.secondary" sx={{ mb: 3 }}>No other orders found for this patient.</Typography>
                            )}

                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Recent Results</Typography>
                            {patientResults?.data?.length > 0 ? (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Test</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Flag</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {patientResults.data.slice(0, 20).map((r, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{r.testId?.testCode || r.testId?.name || '-'}</TableCell>
                                                    <TableCell>{r.numericValue ?? r.textValue ?? r.codedValue ?? '-'}{r.unit ? ` ${r.unit}` : ''}</TableCell>
                                                    <TableCell>{r.flag && <Chip label={r.flag} size="small" color={r.isCritical ? 'error' : 'default'} />}</TableCell>
                                                    <TableCell>{formatDate(r.enteredAt || r.createdAt, tz)}</TableCell>
                                                    <TableCell><Chip label={r.status?.replace(/_/g, ' ')} size="small" sx={{ textTransform: 'capitalize' }} /></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography color="text.secondary">No results found for this patient.</Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Add Tests Dialog */}
            <Dialog open={addTestsOpen} onClose={() => setAddTestsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Tests to Order</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        multiple
                        options={availableNewTests}
                        getOptionLabel={(option) => `${option.code} - ${option.name}${option.kind === 'panel' ? ' (Panel)' : ''}`}
                        isOptionEqualToValue={(o, v) => o.kind === v.kind && o._id === v._id}
                        value={selectedNewTests}
                        onChange={(e, v) => setSelectedNewTests(v)}
                        renderInput={(params) => <TextField {...params} label="Select tests or panels" size="small" sx={{ mt: 1 }} />}
                        renderOption={(props, option) => (
                            <li {...props} key={`${option.kind}-${option._id}`}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                    <Typography variant="body2">
                                        {option.code} - {option.name}
                                        {option.kind === 'panel' && (
                                            <Chip label="Panel" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 18 }} />
                                        )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">{formatCurrency(option.price)}</Typography>
                                </Box>
                            </li>
                        )}
                    />
                    {selectedNewTests.length > 0 && (
                        <Typography sx={{ mt: 1 }} variant="body2" color="text.secondary">
                            Price delta: +{formatCurrency(selectedNewTests.reduce((s, t) => s + (t.price || 0), 0))}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddTestsOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddTests} disabled={loading}>Add</Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Test Dialog */}
            <Dialog open={cancelTestOpen} onClose={() => setCancelTestOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Cancel Test</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Cancel <strong>{cancelTestEntry?.testId?.testCode} - {cancelTestEntry?.testId?.name}</strong>?
                    </Typography>
                    <TextField
                        fullWidth size="small" label="Reason" required
                        value={cancelTestReason}
                        onChange={(e) => setCancelTestReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelTestOpen(false)}>Back</Button>
                    <Button variant="contained" color="error" onClick={handleCancelTest} disabled={loading}>Cancel Test</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Order Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Order</DialogTitle>
                <DialogContent>
                    {/* Details stay editable after signoff (a mis-keyed physician is
                        usually only caught at report time), but result VALUES are not
                        touched here — they go through Amend, which records a reason. */}
                    {['resulted', 'verified'].includes(order.status) && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            This order is {order.status}. Order details can still be corrected here —
                            to change a result value, use <strong>Amend</strong> on the results screen.
                        </Alert>
                    )}
                    <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                        <FormLabel>Priority</FormLabel>
                        <RadioGroup row value={editFields.priority || 'routine'} onChange={(e) => setEditFields({ ...editFields, priority: e.target.value })}>
                            <FormControlLabel value="routine" control={<Radio size="small" />} label="Routine" />
                            <FormControlLabel value="urgent" control={<Radio size="small" color="warning" />} label="Urgent" />
                            <FormControlLabel value="stat" control={<Radio size="small" color="error" />} label="STAT" />
                        </RadioGroup>
                    </FormControl>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={7}>
                            {/* Reaches the report as "Requested By" — techs correct it here when
                                the requesting doctor was mis-keyed at registration. */}
                            <TextField fullWidth size="small" label="Referring Physician" placeholder="Dr. ..."
                                value={editFields.orderingPhysicianName || ''}
                                onChange={(e) => setEditFields({ ...editFields, orderingPhysicianName: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField fullWidth size="small" label="Physician Phone"
                                value={editFields.orderingPhysicianPhone || ''}
                                onChange={(e) => setEditFields({ ...editFields, orderingPhysicianPhone: e.target.value })} />
                        </Grid>
                    </Grid>
                    <DiagnosisInput label="Diagnosis" sx={{ mb: 2 }}
                        value={editFields.diagnosis || ''} onChange={(v) => setEditFields({ ...editFields, diagnosis: v })} />
                    <TextField fullWidth size="small" label="Clinical Notes" multiline rows={2} sx={{ mb: 2 }}
                        value={editFields.clinicalNotes || ''} onChange={(e) => setEditFields({ ...editFields, clinicalNotes: e.target.value })} />
                    <FormControlLabel
                        control={<Switch checked={editFields.isFasting || false} onChange={(e) => setEditFields({ ...editFields, isFasting: e.target.checked })} />}
                        label="Fasting"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditOrder} disabled={loading}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Order Dialog */}
            <Dialog open={cancelOrderOpen} onClose={() => setCancelOrderOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Cancel Order</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to cancel this entire order?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelOrderOpen(false)}>Back</Button>
                    <Button variant="contained" color="error" onClick={confirmCancelOrder}>Cancel Order</Button>
                </DialogActions>
            </Dialog>

            {/* Status Transition Confirmation Dialog */}
            <Dialog open={statusConfirmOpen} onClose={() => setStatusConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Status Change</DialogTitle>
                <DialogContent>
                    <Typography>
                        Change order status to <strong>{pendingStatus?.status}</strong>?
                        {pendingStatus?.status === 'released' && ' This will make the report available to the patient.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" color={pendingStatus?.color || 'primary'} onClick={confirmStatusTransition} disabled={loading}>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Record Payment Dialog */}
            <Dialog open={paymentOpen} onClose={() => setPaymentOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Record Payment</DialogTitle>
                <DialogContent>
                    <TextField fullWidth size="small" label="Amount" type="number" sx={{ mt: 1, mb: 2 }}
                        value={paymentData.amount} onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })} />
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <FormLabel>Method</FormLabel>
                        <Select value={paymentData.method} onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}>
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="insurance">Insurance</MenuItem>
                            <MenuItem value="online">Online</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField fullWidth size="small" label="Reference (optional)" sx={{ mb: 2 }}
                        value={paymentData.reference} onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })} />
                    <TextField fullWidth size="small" label="Notes (optional)" multiline rows={2}
                        value={paymentData.notes} onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handlePayment} disabled={loading}>Record Payment</Button>
                </DialogActions>
            </Dialog>

            {/* Cumulative Report Dialog */}
            <Dialog open={cumulativeOpen} onClose={() => { setCumulativeOpen(false); dispatch(resetGetCumulativeReport()); }} maxWidth="md" fullWidth>
                <DialogTitle>
                    Cumulative Report - {order?.patientId ? `${order.patientId.fName || ''} ${order.patientId.lName || ''}`.trim() : ''}
                </DialogTitle>
                <DialogContent>
                    {cumulativeReport?.data?.tests?.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Test</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Latest Value</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Previous Values</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cumulativeReport.data.tests.map((t, i) => (
                                        <TableRow key={i}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{t.testCode}</Typography>
                                                <Typography variant="caption" color="text.secondary">{t.testName}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {t.results?.[0] ? (
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {t.results[0].value}{t.unit ? ` ${t.unit}` : ''}
                                                            {t.results[0].flag && <Chip label={t.results[0].flag} size="small" color={t.results[0].isCritical ? 'error' : 'default'} sx={{ ml: 0.5 }} />}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">{formatDay(t.results[0].date, tz)}</Typography>
                                                    </Box>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {t.results?.slice(1, 5).map((r, j) => (
                                                    <Typography key={j} variant="caption" display="block" color="text.secondary">
                                                        {r.value}{t.unit ? ` ${t.unit}` : ''} ({formatDay(r.date, tz)})
                                                    </Typography>
                                                ))}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography color="text.secondary" sx={{ mt: 1 }}>No cumulative data available for this patient.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCumulativeOpen(false); dispatch(resetGetCumulativeReport()); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
